/**
 * The list of past (and current) calls: when, in which channel, how long, who,
 * whether there is a transcript to read, and how many proposed tasks are still
 * waiting for someone's decision. Each row opens the call page. Shared by the
 * channel page ("recent calls here") and the all-calls page.
 */
import { Captions, ListChecks, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import type { VoiceCallSummary } from '../../lib/voiceCallsApi';
import { Avatar } from '../tasks/linear/Avatar';
import { LiveBadge } from './VoiceChannelRow';

export function voiceCallPath(callId: string): string {
  return `/team-chat/calls/${encodeURIComponent(callId)}`;
}

export const VOICE_CALLS_PATH = '/team-chat/calls';

const dateFormat = new Intl.DateTimeFormat('ro-RO', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** "1 h 12 min", "12 min", "45 s" — coarse on purpose, it's a list. */
export function useCallDuration(): (ms: number) => string {
  const { t } = useI18n();
  return (ms: number) => {
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) return t('teamChat.voiceDurationH', { h, m });
    if (m > 0) return t('teamChat.voiceDurationM', { m });
    return t('teamChat.voiceDurationS', { s: totalSeconds });
  };
}

export function VoiceCallHistory({
  calls,
  showChannel,
  emptyText,
}: {
  calls: VoiceCallSummary[];
  /** On the all-calls page the channel is the thing that varies; on a channel page it is noise. */
  showChannel: boolean;
  emptyText: string;
}) {
  const { t } = useI18n();
  const duration = useCallDuration();

  if (calls.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {calls.map((call) => {
        const live = call.endedAt === null;
        return (
          <li key={call.id}>
            <Link
              to={voiceCallPath(call.id)}
              className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-accent/60"
            >
              <span
                className={[
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  live ? 'bg-emerald-500/15 text-emerald-600' : 'bg-accent text-muted-foreground',
                ].join(' ')}
              >
                <Volume2 size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {showChannel ? call.channelName : dateFormat.format(new Date(call.startedAt))}
                  </span>
                  {live && <LiveBadge />}
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {showChannel && <>{dateFormat.format(new Date(call.startedAt))} · </>}
                  {duration(call.durationMs)}
                  {' · '}
                  {call.segmentCount > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <Captions size={12} />
                      {t('teamChat.voiceCallSegments', { count: call.segmentCount })}
                    </span>
                  ) : (
                    t('teamChat.voiceCallNoTranscript')
                  )}
                  {call.pendingSuggestionCount > 0 && (
                    <>
                      {' · '}
                      <span className="inline-flex items-center gap-1 font-medium text-brand-700">
                        <ListChecks size={12} />
                        {t('teamChat.voiceCallPendingSuggestions', {
                          count: call.pendingSuggestionCount,
                        })}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <span className="flex shrink-0 -space-x-1.5">
                {call.participants.slice(0, 4).map((p) => (
                  <span key={p.userId} title={p.name} className="rounded-full ring-2 ring-card">
                    <Avatar name={p.name} size={22} />
                  </span>
                ))}
                {call.participants.length > 4 && (
                  <span className="flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
                    +{call.participants.length - 4}
                  </span>
                )}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
